/**
 * The Property map's Aerial layer: daytime aerial imagery as a MapLibre raster
 * source, drawn under the gold outline.
 *
 * Imagery is the one thing MapLibre does not bring with it, and where it comes
 * from is a licensing and cost decision, so it lives here and nowhere else names
 * a provider.
 *
 * The default is USGS NAIP: aerial photography flown in daylight by the USDA at
 * about 0.6 m a pixel, public domain, keyless and open to browser requests. It
 * covers the United States only; outside it the service answers every request
 * with the same blank tile, which would read as imagery that never loaded. So
 * coverage is checked up front (`aerialCovers`) and Aerial is simply not offered
 * where there is none.
 *
 * To use another provider (Esri's Location Platform, Azure Maps, Mapbox) without
 * a code change, set at build time:
 *
 *   VITE_AERIAL_TILE_URL     an XYZ template, key included, e.g. .../{z}/{y}/{x}?token=...
 *   VITE_AERIAL_ATTRIBUTION  the provider's required credit line
 *   VITE_AERIAL_MAXZOOM      the highest zoom with real imagery (default 19)
 *
 * A provider set this way is taken to cover the whole world. Use a key
 * restricted to the platform's own origins: it is visible in the browser.
 */

export type AerialImagery = {
  /** XYZ (or `{bbox-epsg-3857}`) raster tile URL templates. */
  tiles: string[]
  tileSize: number
  /** The highest zoom the provider has real imagery for. MapLibre draws deeper
   *  zooms by scaling this level, rather than asking for tiles that 404. */
  maxzoom: number
  /** Shown in the map's attribution control. The provider's terms require it. */
  attribution: string
  /** Whether this provider has imagery at the point. */
  covers: (lat: number, lng: number) => boolean
  /**
   * Pre-rendered tiles of the same imagery, drawn beneath it. NAIP's service
   * renders each tile when it is asked for: a 3x3 block at city zoom took from
   * 0.05 s to 29 s a tile (30 Oct 2026), and the tiles still on their way showed
   * the pale base map, so Aerial read as one photograph floating on a white
   * page. The underlay answers in well under a second and only goes to zoom 16,
   * so the slow source is asked only for the detail beyond it (`minzoom`).
   */
  underlay?: { tiles: string[]; maxzoom: number; minzoom: number; attribution: string }
}

/** The source id and layer id the map uses, so tests and the outline layers can name them. */
export const AERIAL_SOURCE_ID = 'aerial-imagery'
export const AERIAL_LAYER_ID = 'aerial-imagery-layer'
export const AERIAL_UNDERLAY_SOURCE_ID = 'aerial-imagery-underlay'
export const AERIAL_UNDERLAY_LAYER_ID = 'aerial-imagery-underlay-layer'

/** How long imagery may take to appear before the view gives up on it. */
export const AERIAL_LOAD_TIMEOUT_MS = 12_000

/**
 * A coarse outline of the contiguous United States, [lng, lat], offshore on the
 * coasts and down the middle of the Great Lakes and the St. Lawrence. Good to
 * about 20 km at the borders: a Toronto, Ottawa, Montreal or Vancouver address
 * is outside it, and an address within a few kilometres of the border may be
 * wrongly outside too, which only means it is offered no Aerial.
 */
const CONUS: ReadonlyArray<readonly [number, number]> = [
  [-125.6, 48.5], [-123.2, 48.3], [-123.0, 48.95], [-95.15, 48.99], [-94.6, 48.72],
  [-93.0, 48.55], [-91.4, 48.1], [-89.6, 47.9], [-88.4, 48.25], [-85.0, 47.2],
  [-84.4, 46.55], [-83.9, 45.9], [-82.6, 45.3], [-82.4, 43.0], [-83.1, 42.25],
  [-82.6, 41.8], [-81.0, 41.95], [-79.1, 42.75], [-78.9, 43.3], [-78.0, 43.6],
  [-76.4, 44.1], [-75.3, 44.85], [-74.7, 44.99], [-71.5, 44.99], [-70.8, 45.4],
  [-70.3, 46.4], [-69.2, 47.45], [-68.3, 47.37], [-67.8, 47.07], [-67.7, 45.7],
  [-67.0, 44.7], [-65.0, 43.5], [-65.0, 25.0], [-79.5, 24.0], [-83.0, 24.0],
  [-90.0, 25.6], [-97.2, 25.8], [-99.1, 26.4], [-100.0, 27.5], [-101.4, 29.7],
  [-102.5, 29.7], [-104.5, 29.6], [-106.5, 31.7], [-108.2, 31.7], [-108.2, 31.3],
  [-111.1, 31.3], [-114.8, 32.5], [-117.1, 32.5], [-118.5, 32.2], [-125.6, 38.0],
]

/** Hawaii: NAIP is flown there too. */
const HAWAII = { south: 18.8, north: 22.4, west: -160.8, east: -154.7 }

function inside(lat: number, lng: number, ring: ReadonlyArray<readonly [number, number]>): boolean {
  let within = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const [xi, yi] = ring[i]
    const [xj, yj] = ring[j]
    if (yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) within = !within
  }
  return within
}

/** Whether NAIP has imagery at this point: the contiguous states and Hawaii. */
export function naipCovers(lat: number, lng: number): boolean {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false
  if (lat >= HAWAII.south && lat <= HAWAII.north && lng >= HAWAII.west && lng <= HAWAII.east) return true
  return inside(lat, lng, CONUS)
}

const NAIP: AerialImagery = {
  tiles: [
    'https://imagery.nationalmap.gov/arcgis/rest/services/USGSNAIPImagery/ImageServer/exportImage'
      + '?bbox={bbox-epsg-3857}&bboxSR=3857&imageSR=3857&size=256,256&format=jpg&f=image',
  ],
  tileSize: 256,
  // About 0.6 m a pixel is the native detail; MapLibre scales it for the zooms beyond.
  maxzoom: 18,
  attribution: 'Imagery: USDA NAIP, USGS The National Map',
  covers: naipCovers,
  underlay: {
    // The National Map's cached orthoimagery (USDA NAIP at these zooms), public
    // domain and keyless like NAIP itself. Its cache ends at zoom 16.
    tiles: ['https://basemap.nationalmap.gov/arcgis/rest/services/USGSImageryOnly/MapServer/tile/{z}/{y}/{x}'],
    maxzoom: 16,
    minzoom: 15,
    attribution: 'USDA, USGS The National Map: Orthoimagery',
  },
}

function configured(): AerialImagery | null {
  const env = import.meta.env as Record<string, string | undefined>
  const url = env.VITE_AERIAL_TILE_URL?.trim()
  if (!url) return null
  const maxzoom = Number(env.VITE_AERIAL_MAXZOOM)
  return {
    tiles: [url],
    tileSize: 256,
    maxzoom: Number.isFinite(maxzoom) && maxzoom > 0 ? maxzoom : 19,
    attribution: env.VITE_AERIAL_ATTRIBUTION?.trim() || 'Aerial imagery',
    covers: (lat, lng) => Number.isFinite(lat) && Number.isFinite(lng),
  }
}

export const AERIAL_IMAGERY: AerialImagery = configured() ?? NAIP

export function aerialCovers(lat: number, lng: number): boolean {
  return AERIAL_IMAGERY.covers(lat, lng)
}

/**
 * How far the map may be recentred and still be looking at imagery it has already
 * loaded. A coordinate that is refined for the same address (a rooftop where a
 * street centroid was) moves the pin by tens of metres; the tiles under it are
 * the ones already on screen, so no new tile arrives to be waited for.
 */
export const AERIAL_SAME_VIEW_M = 250

/** Great-circle distance in metres, flat-earth accurate at these distances. */
export function metresApart(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const perDegree = 111_320
  const dLat = (b.lat - a.lat) * perDegree
  const dLng = (b.lng - a.lng) * perDegree * Math.cos(((a.lat + b.lat) / 2) * (Math.PI / 180))
  return Math.hypot(dLat, dLng)
}

export function aerialSource(imagery: AerialImagery) {
  return {
    type: 'raster' as const,
    tiles: imagery.tiles,
    tileSize: imagery.tileSize,
    maxzoom: imagery.maxzoom,
    attribution: imagery.attribution,
  }
}

/** The pre-rendered underlay's raster source, or null for a provider that has none. */
export function aerialUnderlaySource(imagery: AerialImagery) {
  if (!imagery.underlay) return null
  return {
    type: 'raster' as const,
    tiles: imagery.underlay.tiles,
    tileSize: 256,
    maxzoom: imagery.underlay.maxzoom,
    attribution: imagery.underlay.attribution,
  }
}

/** Half the circumference of the Web Mercator world, in metres. */
const MERCATOR_HALF = 20037508.342789244

/**
 * One aerial photograph of the ground around a point, `widthM` metres across, as
 * an image URL: the Property photos panel's area view for a deal that states only
 * its city. The same imagery host and switch as the Aerial map layer, asked once
 * for a whole picture instead of for tiles.
 *
 * Only an imagery service that renders an arbitrary box can answer this (NAIP's
 * `exportImage` does); a provider set by `VITE_AERIAL_TILE_URL` is an XYZ tile
 * template, and there is no single picture to ask it for: null.
 */
export function aerialSnapshotUrl(
  lat: number,
  lng: number,
  { widthM = 2400, widthPx = 800, heightPx = 500 }: { widthM?: number; widthPx?: number; heightPx?: number } = {},
  imagery: AerialImagery = AERIAL_IMAGERY,
): string | null {
  const template = imagery.tiles[0] ?? ''
  if (!template.includes('{bbox-epsg-3857}') || !imagery.covers(lat, lng)) return null
  const x = (lng * MERCATOR_HALF) / 180
  const y = (Math.log(Math.tan(((90 + lat) * Math.PI) / 360)) * MERCATOR_HALF) / Math.PI
  // Mercator stretches the ground by 1 / cos(latitude): a box that is `widthM`
  // across on the ground is wider than that on the projection.
  const halfW = widthM / Math.cos((lat * Math.PI) / 180) / 2
  const halfH = (halfW * heightPx) / widthPx
  const bbox = [x - halfW, y - halfH, x + halfW, y + halfH].map((v) => v.toFixed(1)).join(',')
  return template.replace('{bbox-epsg-3857}', bbox).replace(/size=\d+,\d+/, `size=${widthPx},${heightPx}`)
}
